import React, { useState } from 'react';
import {
  ActionIcon, Autocomplete, Badge, Button, Checkbox, Group,
  Paper, Stack, Text, TextInput, Tooltip,
} from '@mantine/core';
import { useAppContext } from '../contexts/AppContext';
import { ROLLEN } from '../utils/rollen';

const TOESTEMMINGSTEKST =
  'Dit overleg wordt opgenomen om automatisch een gespreksverslag en lokatierapportage op te stellen. ' +
  'De opname wordt uitsluitend daarvoor gebruikt, er wordt niet vastgelegd wie wat zegt, en het geluidsbestand ' +
  'wordt verwijderd zodra het verslag is gecontroleerd (uiterlijk na 30 dagen). Met het starten van de sessie ' +
  'bevestigt de facilitator dat alle aanwezigen hiermee instemmen.';

const LEGE_DEELNEMER = { naam: '', organisatie: '', rol: '' };

const balkPositie = {
  position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
  zIndex: 15, maxWidth: '92vw',
};

export default function SessieBalk() {
  const {
    sessie, level, selectedBuurt, selectedProject, setSelectedProject, fetchWithAuth,
  } = useAppContext();
  const [formOpen, setFormOpen] = useState(false);
  const [deelnemers, setDeelnemers] = useState([{ ...LEGE_DEELNEMER }]);
  const [toestemming, setToestemming] = useState(false);

  // Formulier openen: deelnemers voorvullen met de betrokken ambtenaren van de
  // gemeente van deze buurt — zowel de geregistreerde gebruikers die eraan
  // gekoppeld zijn als de handmatig vastgelegde contacten. Aanpassen/
  // verwijderen/aanvullen geldt alleen voor deze belangentafel; de
  // gemeentelijst zelf blijft ongemoeid.
  const openFormulier = async () => {
    setFormOpen(true);
    const gemeentecode = selectedBuurt?.gemeentecode;
    if (!gemeentecode) return;
    const gemeenteNaam = selectedBuurt.gemeentenaam || 'Gemeente';
    // De functierol verraadt de organisatie (bv. 'Omgevingsmanager Stedin');
    // anders hoort iemand bij de gemeente van deze buurt.
    const organisatieVoor = (rol) => {
      const r = (rol || '').toLowerCase();
      if (r.includes('stedin')) return 'Stedin';
      if (r.includes('aannemer')) return 'Aannemer';
      return gemeenteNaam;
    };
    try {
      const res = await fetchWithAuth(`/api/gemeenten/${encodeURIComponent(gemeentecode)}/contacten`);
      const data = await res.json();
      if (!data.success) return;
      const uitGeregistreerden = (data.geregistreerden || []).map(g => ({
        naam: g.naam, organisatie: organisatieVoor(g.functie), rol: g.functie || '',
      }));
      const uitContacten = (data.contacten || []).map(c => ({
        naam: c.naam, organisatie: organisatieVoor(c.functie), rol: c.functie || '',
      }));
      // Geregistreerden eerst; dubbele namen (handmatig én geregistreerd) 1x
      const gezien = new Set();
      const alle = [...uitGeregistreerden, ...uitContacten].filter(d => {
        const sleutel = (d.naam || '').trim().toLowerCase();
        if (!sleutel || gezien.has(sleutel)) return false;
        gezien.add(sleutel);
        return true;
      });
      if (alle.length) setDeelnemers(alle);
    } catch (e) {
      // Voorvullen is gemak, geen vereiste: bij een fout gewoon leeg beginnen
    }
  };

  const verwijderDeelnemer = (i) => {
    setDeelnemers(prev => {
      const rest = prev.filter((_, idx) => idx !== i);
      return rest.length ? rest : [{ ...LEGE_DEELNEMER }];
    });
  };

  if (!sessie) return null;
  const { actieveSessie, bezig, opnameActief, startSessie, stopSessie } = sessie;

  // Zolang er een sessie loopt is de balk op élk niveau zichtbaar (de opname
  // loopt immers door); starten kan alleen vanaf buurt- of projectniveau.
  const opBuurtOfProject = level === 'buurt' || level === 'project';
  if (!actieveSessie && !opBuurtOfProject) return null;

  const zetDeelnemer = (i, veld, waarde) => {
    setDeelnemers(prev => prev.map((d, idx) => (idx === i ? { ...d, [veld]: waarde } : d)));
  };

  const handleStart = async () => {
    const ingevuld = deelnemers.filter(d => d.naam.trim());
    const ok = await startSessie(ingevuld);
    if (ok) {
      setFormOpen(false);
      setDeelnemers([{ ...LEGE_DEELNEMER }]);
      setToestemming(false);
    }
  };

  const kanStarten = toestemming && !bezig && deelnemers.some(d => d.naam.trim());

  if (actieveSessie) {
    const buitenBuurt = !opBuurtOfProject;
    return (
      <Paper p="sm" shadow="md" radius="md" withBorder style={balkPositie}>
        <Group gap="md" wrap="nowrap">
          <Badge color="red" variant="dot" size="lg">
            Belangentafel {actieveSessie.belangentafel_nr}
          </Badge>
          <Tooltip
            label={opnameActief
              ? 'Opname loopt'
              : 'Geen opname actief — geen microfoon of pagina herladen tijdens de sessie'}
            withArrow
          >
            <Text size="sm" style={{ cursor: 'default' }}>
              {opnameActief ? '🎙️' : '🔇'}
            </Text>
          </Tooltip>
          {buitenBuurt ? (
            <Text size="sm" c="orange" fw={500}>
              Buiten de sessie-buurt — opname loopt door
            </Text>
          ) : selectedProject ? (
            <Group gap={4} wrap="nowrap">
              <Text size="sm" c="dimmed">Bespreekt: station {selectedProject.projectnummer}</Text>
              <Tooltip label="Station afsluiten (bespreking terug naar buurtniveau)" withArrow>
                <ActionIcon
                  size="sm" variant="subtle" color="gray"
                  aria-label="Station afsluiten"
                  onClick={() => setSelectedProject(null)}
                >
                  ✕
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : (
            <Text size="sm" c="dimmed">Geen station geselecteerd</Text>
          )}
          <Button color="red" size="xs" onClick={stopSessie} loading={bezig}>
            Stop sessie
          </Button>
        </Group>
      </Paper>
    );
  }

  if (!formOpen) {
    return (
      <Paper p="sm" shadow="md" radius="md" withBorder style={balkPositie}>
        <Group gap="md" wrap="nowrap">
          <Button onClick={openFormulier}>Belangentafel starten</Button>
          {selectedBuurt && (
            <Text size="sm" c="dimmed">{selectedBuurt.buurtnaam || selectedBuurt.buurtcode}</Text>
          )}
        </Group>
      </Paper>
    );
  }

  return (
    <Paper p="md" shadow="md" radius="md" withBorder style={{ ...balkPositie, width: 'min(580px, 92vw)' }}>
      <Stack gap="sm">
        <Text fw={600}>Belangentafel starten</Text>
        <Text size="sm" c="dimmed">Deelnemers</Text>
        {deelnemers.map((d, i) => (
          <Group key={i} gap="xs" wrap="nowrap">
            <TextInput
              placeholder="Naam" value={d.naam} style={{ flex: 1 }}
              onChange={e => zetDeelnemer(i, 'naam', e.currentTarget.value)}
            />
            <TextInput
              placeholder="Organisatie" value={d.organisatie} style={{ flex: 1 }}
              onChange={e => zetDeelnemer(i, 'organisatie', e.currentTarget.value)}
            />
            <Autocomplete
              placeholder="Rol" value={d.rol} data={ROLLEN} style={{ flex: 1 }}
              onChange={waarde => zetDeelnemer(i, 'rol', waarde)}
            />
            <ActionIcon
              variant="subtle" color="gray" size="sm"
              aria-label={`Verwijder deelnemer ${i + 1}`}
              onClick={() => verwijderDeelnemer(i)}
            >
              ✕
            </ActionIcon>
          </Group>
        ))}
        <Group justify="space-between">
          <Button
            variant="subtle" size="xs"
            onClick={() => setDeelnemers(prev => [...prev, { ...LEGE_DEELNEMER }])}
          >
            + deelnemer toevoegen
          </Button>
        </Group>
        <Checkbox
          checked={toestemming}
          onChange={e => setToestemming(e.currentTarget.checked)}
          label={<Text size="xs">{TOESTEMMINGSTEKST}</Text>}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setFormOpen(false)}>Annuleren</Button>
          <Button onClick={handleStart} disabled={!kanStarten} loading={bezig}>Start</Button>
        </Group>
      </Stack>
    </Paper>
  );
}
